import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { GET_PASS_BY_ID, GET_PASS_PURCHASES } from "../graphql/query";
import type { GetPassByIdResponse } from "../interfaces/pass.interface";
import type { GetPassPurchasesResponse } from "../interfaces/studentPassPurchase.interface";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PASS_TYPE_LABELS: Record<string, string> = {
  DROP_IN: "Drop In",
  WEEKLY_4_CLASSES: "Weekly",
  MONTHLY_4_CLASSES: "Monthly",
  "Drop-in": "Drop In",
  "Weekly": "Weekly",
  "Monthly": "Monthly",
};

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "S";
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionCard({ children, sx = {} }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E9EEF5",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid #F0F4F8",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#F0F4F8", color: "#6B7280" }}
        />
      )}
    </Box>
  );
}

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 2,
        borderRadius: "12px",
        bgcolor: "#FAFBFC",
        border: "1px solid #F0F4F8",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          bgcolor: "#EEF7E9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4CAF50",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 13, mt: 0.25, wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Overview Section ─────────────────────────────────────────────────────────

function formatValidity(pass: any) {
  const days = pass.validityDays || 0;
  if (days <= 0) return "—";
  const type = pass.passType;
  if (type === "Weekly" && days % 7 === 0) {
    const n = days / 7;
    return `${n} week${n > 1 ? "s" : ""}`;
  }
  if (type === "Monthly" && days % 30 === 0) {
    const n = days / 30;
    return `${n} month${n > 1 ? "s" : ""}`;
  }
  return `${days} day${days > 1 ? "s" : ""}`;
}

function OverviewSection({ pass }: { pass: any }) {
  const detailItems = [
    { icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />, label: "Pass Type", value: PASS_TYPE_LABELS[pass.passType] || pass.passType || "—" },
    { icon: <SchoolOutlinedIcon sx={{ fontSize: 18 }} />, label: "No. of Classes", value: pass.noOfClasses?.toString() || "—" },
    { icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />, label: "Validity", value: formatValidity(pass) },
    { icon: <Typography sx={{ fontSize: 18, fontWeight: 900 }}>₹</Typography>, label: "Pass Cost", value: pass.passCost ? `₹${pass.passCost}` : "—" },
    { icon: <SchoolOutlinedIcon sx={{ fontSize: 18 }} />, label: "Linked Classes", value: (pass.classNames && pass.classNames.length ? pass.classNames.join(", ") : pass.className) || "—" },
    { icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />, label: "Style Type", value: pass.styleType || "—" },
  ];

  const instructors: string[] = pass.instructors || [];
  const offers: any[] = pass.offers || [];

  return (
    <Stack spacing={2}>
      <SectionCard>
        <SectionHeader title="Pass Details" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {detailItems.map((item) => (
            <InfoItem key={item.label} icon={item.icon} label={item.label} value={item.value} />
          ))}
        </Box>

        {instructors.length > 0 && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#FAFBFC", border: "1px solid #F0F4F8", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "#EEF7E9", display: "flex", alignItems: "center", justifyContent: "center", color: "#4CAF50", flexShrink: 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10, display: "block" }}>
                  Instructors
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                  {instructors.map((inst: string) => (
                    <Chip
                      key={inst}
                      label={inst}
                      size="small"
                      sx={{ height: 24, fontSize: 12, fontWeight: 700, bgcolor: "#EEF7E9", color: "#2E7D32", border: "1px solid #C8E6C9" }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {pass.description && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#FAFBFC", border: "1px solid #F0F4F8", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "#EEF7E9", display: "flex", alignItems: "center", justifyContent: "center", color: "#4CAF50", flexShrink: 0 }}>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}>
                  Description
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 500, mt: 0.5, lineHeight: 1.7 }}>
                  {pass.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>

      {offers.length > 0 && (
        <SectionCard>
          <SectionHeader title="Linked Offers" count={offers.length} />
          <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {offers.map((offer: any, idx: number) => (
              <Box
                key={idx}
                sx={{ border: "1px solid #E9EEF5", borderRadius: "14px", overflow: "hidden", bgcolor: "#FAFBFC" }}
              >
                <Box sx={{ height: 4, bgcolor: offer.isActive ? "#4CAF50" : "#9CA3AF", borderRadius: "14px 14px 0 0" }} />
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: "#4CAF50" }} />
                      <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>{offer.offerName}</Typography>
                    </Box>
                    <Chip
                      label={offer.isActive ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        height: 22, fontWeight: 800, fontSize: 10,
                        bgcolor: offer.isActive ? "#E8F5E9" : "#F3F4F6",
                        color: offer.isActive ? "#2E7D32" : "#6B7280",
                      }}
                    />
                  </Stack>
                  <Divider sx={{ borderColor: "#F0F4F8", mb: 1.5 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>Code</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#4CAF50" }}>{offer.offerCode}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>Discount</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{offer.discount}%</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>Valid Till</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{formatDate(offer.validTill)}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}

      <SectionCard>
        <SectionHeader title="Record Info" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <InfoItem icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />} label="Created" value={formatDate(pass.created_at)} />
          <InfoItem icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />} label="Last Updated" value={formatDate(pass.updated_at)} />
        </Box>
      </SectionCard>
    </Stack>
  );
}

// ─── Purchases Section ────────────────────────────────────────────────────────

const PURCHASES_PAGE_SIZE = 8;

function PurchasesSection({ purchases }: { purchases: any[] }) {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(purchases.length / PURCHASES_PAGE_SIZE);
  const paged = purchases.slice((page - 1) * PURCHASES_PAGE_SIZE, page * PURCHASES_PAGE_SIZE);

  if (purchases.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <GroupOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No students have purchased this pass yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <SectionHeader title="Student Purchases" count={purchases.length} />
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {paged.map((purchase: any, idx: number) => (
          <Box
            key={purchase.id || idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              borderRadius: "12px",
              border: "1px solid #F0F4F8",
              bgcolor: "#FAFBFC",
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: "#a3d65c",
                color: "#fff",
                fontWeight: 900,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {initials(purchase.studentName || "")}
            </Avatar>
            <Box flex={1} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }} noWrap>
                {purchase.studentName || "—"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                {purchase.passDbId} · Purchased {formatDate(purchase.purchasedAt)}
                {(() => {
                  const classes =
                    purchase.classNames && purchase.classNames.length
                      ? purchase.classNames
                      : purchase.className
                      ? [purchase.className]
                      : [];
                  return classes.length ? ` · ${classes.join(", ")}` : "";
                })()}
              </Typography>
            </Box>
            <Chip
              label={purchase.status === "ACTIVE" ? "Active" : purchase.status === "EXPIRED" ? "Expired" : purchase.status || "—"}
              size="small"
              icon={
                purchase.status === "ACTIVE"
                  ? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
                  : <CancelOutlinedIcon sx={{ fontSize: "14px !important" }} />
              }
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: 11,
                flexShrink: 0,
                bgcolor: purchase.status === "ACTIVE" ? "#E8F5E9" : "#FEF2F2",
                color: purchase.status === "ACTIVE" ? "#2E7D32" : "#B91C1C",
              }}
            />
          </Box>
        ))}
      </Box>
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F0F4F8" }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#a3d65c !important", color: "#fff" } }}
          />
        </Box>
      )}
    </SectionCard>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { label: "Overview", icon: <CardMembershipOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Purchases", icon: <GroupOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const PassReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { data: passData, loading: passLoading } = useQuery<GetPassByIdResponse>(
    GET_PASS_BY_ID,
    { variables: { id }, skip: !id, fetchPolicy: "network-only" }
  );

  const { data: purchasesData, loading: purchasesLoading } = useQuery<GetPassPurchasesResponse>(
    GET_PASS_PURCHASES,
    { variables: { passId: id }, skip: !id, fetchPolicy: "network-only" }
  );

  const isLoading = passLoading || purchasesLoading;
  const pass = passData?.getPassById?.data;
  const purchases = purchasesData?.getPassPurchases?.data ?? [];

  const quickStats = useMemo(() => ({
    totalPurchases: purchases.length,
    active: purchases.filter((p: any) => p.status === "ACTIVE").length,
    expired: purchases.filter((p: any) => p.status === "EXPIRED").length,
  }), [purchases]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={36} sx={{ color: "#a3d65c" }} />
      </Box>
    );
  }

  if (!pass) {
    return (
      <Box sx={{ mt: 8, textAlign: "center" }}>
        <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>Pass not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 }, px: { xs: 1, md: 3 }, pb: 6 }}>
      {/* Back link */}
      <Box
        onClick={() => navigate("/passes")}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          color: "#6B7280",
          fontSize: 13,
          fontWeight: 600,
          mb: 2,
          "&:hover": { color: "#111827" },
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
        Back to Passes
      </Box>

      {/* ── Hero Card ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "20px",
          overflow: "hidden",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2.5} alignItems={{ sm: "center" }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "18px",
                bgcolor: "#EEF7E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                border: "2px solid #C8E6C9",
                boxShadow: "0 4px 14px rgba(163,214,92,0.25)",
              }}
            >
              <CardMembershipOutlinedIcon sx={{ fontSize: 34, color: "#4CAF50" }} />
            </Box>

            <Box flex={1}>
              <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#111827", lineHeight: 1.2 }}>
                  {pass.passName}
                </Typography>
                <Chip
                  label={pass.status === "Active" ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    height: 24, fontWeight: 800, fontSize: 11,
                    bgcolor: pass.status === "Active" ? "#E8F5E9" : "#FEF2F2",
                    color: pass.status === "Active" ? "#2E7D32" : "#B91C1C",
                  }}
                />
                <Chip
                  label={PASS_TYPE_LABELS[pass.passType] || pass.passType}
                  size="small"
                  sx={{ height: 24, fontWeight: 800, fontSize: 11, bgcolor: "#EBF2FF", color: "#1565C0" }}
                />
              </Stack>
              <Typography sx={{ color: "#9CA3AF", fontWeight: 600, fontSize: 13, mt: 0.25 }}>
                {pass.passId} · {pass.noOfClasses} class{pass.noOfClasses !== 1 ? "es" : ""} · ₹{pass.passCost}
              </Typography>
            </Box>
          </Stack>

          {/* Quick stats strip */}
          <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
            {[
              { label: "Total Purchases", value: quickStats.totalPurchases, color: "#1565C0", bg: "#EBF2FF" },
              { label: "Active", value: quickStats.active, color: "#4CAF50", bg: "#E8F5E9" },
              { label: "Expired", value: quickStats.expired, color: "#E53935", bg: "#FEF2F2" },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  textAlign: "center",
                  py: 1.75,
                  px: 1,
                  borderRadius: "12px",
                  bgcolor: s.bg,
                  border: `1px solid ${s.color}22`,
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 24, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", fontSize: 11 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "14px",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: 13.5, color: "#9CA3AF", minHeight: 48, gap: 0.75 },
            "& .Mui-selected": { color: "#111827 !important", fontWeight: 800 },
            "& .MuiTabs-indicator": { backgroundColor: "#a3d65c", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {TABS.map((t, i) => {
            const counts = [undefined, purchases.length];
            const count = counts[i];
            return (
              <Tab key={t.label} label={count !== undefined ? `${t.label} (${count})` : t.label} icon={t.icon} iconPosition="start" />
            );
          })}
        </Tabs>
      </Box>

      {/* ── Tab content ── */}
      {activeTab === 0 && <OverviewSection pass={pass} />}
      {activeTab === 1 && <PurchasesSection purchases={purchases} />}
    </Box>
  );
};
export default PassReport;
